import React from 'react';
import defaultCv from '../data/defaultPortfolioCv';

const emptyContact = { text: '', href: '' };

const CV_SECTION_PRESETS = [
  {
    key: 'education',
    createLabel: 'Education',
    matchLabels: ['education'],
    type: 'entries',
    emptyEntry: () => ({ title: '', subtitle: '', meta: '', date: '', details: [] })
  },
  {
    key: 'experience',
    createLabel: 'Experience',
    matchLabels: ['experience', 'employment'],
    type: 'entries',
    emptyEntry: () => ({ title: '', subtitle: '', meta: '', date: '', details: [''] })
  },
  {
    key: 'skills',
    createLabel: 'Skills',
    matchLabels: ['skills'],
    type: 'list',
    emptyEntry: null
  },
  {
    key: 'certifications',
    createLabel: 'Certifications',
    matchLabels: ['certifications', 'certification'],
    type: 'entries',
    emptyEntry: () => ({ title: '', subtitle: '', meta: '', date: '', details: [] })
  }
];

const emptySection = { label: '', type: 'entries', entries: [], items: [], footer: '' };

export function createEmptyCv() {
  return JSON.parse(JSON.stringify(defaultCv));
}

function moveItem(list, index, direction) {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function normalizeLabel(label) {
  return String(label || '').trim().toLowerCase();
}

function findPresetForSection(section) {
  const label = normalizeLabel(section?.label);
  return CV_SECTION_PRESETS.find((preset) => preset.matchLabels.includes(label)) || null;
}

function findSectionIndex(sections, presetKey) {
  const preset = CV_SECTION_PRESETS.find((item) => item.key === presetKey);
  if (!preset) return -1;
  return (sections || []).findIndex((section) => preset.matchLabels.includes(normalizeLabel(section?.label)));
}

function sortSectionIndices(sections) {
  const indices = (sections || []).map((_, index) => index);
  const presetOrder = (index) => {
    const preset = findPresetForSection(sections[index]);
    if (!preset) return CV_SECTION_PRESETS.length + index;
    return CV_SECTION_PRESETS.findIndex((item) => item.key === preset.key);
  };
  return indices.sort((a, b) => presetOrder(a) - presetOrder(b));
}

function isExperienceSection(section) {
  const preset = findPresetForSection(section);
  return preset?.key === 'experience';
}

const fieldStyle = { width: '100%', boxSizing: 'border-box', background: '#fff', color: '#000', border: '1px solid #000' };
const blockStyle = { border: '1px solid #000', borderRadius: 0, padding: 12, marginBottom: 10, background: '#fff', color: '#000' };
const btnStyle = { marginRight: 6, marginTop: 4, cursor: 'pointer' };

export default function PortfolioCvEditor({ cv, onChange }) {
  const sections = cv.sections || [];

  const updateProfile = (key, value) => {
    onChange({ ...cv, profile: { ...cv.profile, [key]: value } });
  };

  const updateContact = (index, key, value) => {
    const contact = [...(cv.profile?.contact || [])];
    contact[index] = { ...contact[index], [key]: value };
    updateProfile('contact', contact);
  };

  const addContact = () => {
    updateProfile('contact', [...(cv.profile?.contact || []), { ...emptyContact }]);
  };

  const removeContact = (index) => {
    updateProfile('contact', (cv.profile?.contact || []).filter((_, i) => i !== index));
  };

  const moveContact = (index, direction) => {
    updateProfile('contact', moveItem(cv.profile?.contact || [], index, direction));
  };

  const updateIntro = (index, value) => {
    const intro = [...(cv.profile?.intro || [])];
    intro[index] = value;
    updateProfile('intro', intro);
  };

  const addIntro = () => {
    updateProfile('intro', [...(cv.profile?.intro || []), '']);
  };

  const removeIntro = (index) => {
    updateProfile('intro', (cv.profile?.intro || []).filter((_, i) => i !== index));
  };

  const moveIntro = (index, direction) => {
    updateProfile('intro', moveItem(cv.profile?.intro || [], index, direction));
  };

  const updateSection = (index, patch) => {
    const nextSections = [...sections];
    nextSections[index] = { ...nextSections[index], ...patch };
    onChange({ ...cv, sections: nextSections });
  };

  const ensureSection = (presetKey) => {
    const preset = CV_SECTION_PRESETS.find((item) => item.key === presetKey);
    if (!preset) return sections;

    const existingIndex = findSectionIndex(sections, presetKey);
    if (existingIndex >= 0) {
      const nextSections = [...sections];
      if (normalizeLabel(nextSections[existingIndex].label) !== normalizeLabel(preset.createLabel)) {
        nextSections[existingIndex] = { ...nextSections[existingIndex], label: preset.createLabel };
      }
      return nextSections;
    }

    const newSection =
      preset.type === 'list'
        ? { label: preset.createLabel, type: 'list', items: [], footer: '' }
        : { label: preset.createLabel, type: 'entries', entries: [] };

    return [...sections, newSection];
  };

  const addSection = () => {
    onChange({
      ...cv,
      sections: [...sections, { ...emptySection, entries: [{ title: '', subtitle: '', meta: '', date: '', details: [''] }] }]
    });
  };

  const removeSection = (index) => {
    onChange({ ...cv, sections: sections.filter((_, i) => i !== index) });
  };

  const moveSection = (index, direction) => {
    onChange({ ...cv, sections: moveItem(sections, index, direction) });
  };

  const updateEntry = (sectionIndex, entryIndex, key, value) => {
    const nextSections = [...sections];
    const entries = [...(nextSections[sectionIndex].entries || [])];
    entries[entryIndex] = { ...entries[entryIndex], [key]: value };
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], entries };
    onChange({ ...cv, sections: nextSections });
  };

  const updateEntryDetail = (sectionIndex, entryIndex, detailIndex, value) => {
    const nextSections = [...sections];
    const entries = [...(nextSections[sectionIndex].entries || [])];
    const details = [...(entries[entryIndex].details || [])];
    details[detailIndex] = value;
    entries[entryIndex] = { ...entries[entryIndex], details };
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], entries };
    onChange({ ...cv, sections: nextSections });
  };

  const addEntry = (sectionIndex) => {
    const section = sections[sectionIndex];
    const preset = findPresetForSection(section);
    const emptyEntry = preset?.emptyEntry?.() || { title: '', subtitle: '', meta: '', date: '', details: [''] };
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      entries: [...(nextSections[sectionIndex].entries || []), emptyEntry]
    };
    onChange({ ...cv, sections: nextSections });
  };

  const quickAddToPreset = (presetKey) => {
    const preset = CV_SECTION_PRESETS.find((item) => item.key === presetKey);
    if (!preset) return;

    const nextSections = ensureSection(presetKey);
    const sectionIndex = findSectionIndex(nextSections, presetKey);
    if (sectionIndex < 0) return;

    if (preset.type === 'list') {
      nextSections[sectionIndex] = {
        ...nextSections[sectionIndex],
        items: [...(nextSections[sectionIndex].items || []), '']
      };
    } else {
      nextSections[sectionIndex] = {
        ...nextSections[sectionIndex],
        entries: [...(nextSections[sectionIndex].entries || []), preset.emptyEntry()]
      };
    }

    onChange({ ...cv, sections: nextSections });
  };

  const removeEntry = (sectionIndex, entryIndex) => {
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      entries: (nextSections[sectionIndex].entries || []).filter((_, i) => i !== entryIndex)
    };
    onChange({ ...cv, sections: nextSections });
  };

  const moveEntry = (sectionIndex, entryIndex, direction) => {
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      entries: moveItem(nextSections[sectionIndex].entries || [], entryIndex, direction)
    };
    onChange({ ...cv, sections: nextSections });
  };

  const addEntryDetail = (sectionIndex, entryIndex) => {
    const nextSections = [...sections];
    const entries = [...(nextSections[sectionIndex].entries || [])];
    entries[entryIndex] = {
      ...entries[entryIndex],
      details: [...(entries[entryIndex].details || []), '']
    };
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], entries };
    onChange({ ...cv, sections: nextSections });
  };

  const removeEntryDetail = (sectionIndex, entryIndex, detailIndex) => {
    const nextSections = [...sections];
    const entries = [...(nextSections[sectionIndex].entries || [])];
    entries[entryIndex] = {
      ...entries[entryIndex],
      details: (entries[entryIndex].details || []).filter((_, i) => i !== detailIndex)
    };
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], entries };
    onChange({ ...cv, sections: nextSections });
  };

  const updateListItem = (sectionIndex, itemIndex, value) => {
    const nextSections = [...sections];
    const items = [...(nextSections[sectionIndex].items || [])];
    items[itemIndex] = value;
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], items };
    onChange({ ...cv, sections: nextSections });
  };

  const addListItem = (sectionIndex) => {
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      items: [...(nextSections[sectionIndex].items || []), '']
    };
    onChange({ ...cv, sections: nextSections });
  };

  const removeListItem = (sectionIndex, itemIndex) => {
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      items: (nextSections[sectionIndex].items || []).filter((_, i) => i !== itemIndex)
    };
    onChange({ ...cv, sections: nextSections });
  };

  const moveListItem = (sectionIndex, itemIndex, direction) => {
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      items: moveItem(nextSections[sectionIndex].items || [], itemIndex, direction)
    };
    onChange({ ...cv, sections: nextSections });
  };

  const sortedSectionIndices = sortSectionIndices(sections);

  return (
    <div>
      <h4 style={{ marginTop: 0 }}>Profile</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <input style={fieldStyle} placeholder="Name" value={cv.profile?.name || ''} onChange={(e) => updateProfile('name', e.target.value)} />
        <input style={fieldStyle} placeholder="Label (e.g. Profile)" value={cv.profile?.label || ''} onChange={(e) => updateProfile('label', e.target.value)} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <strong>Contact lines</strong>
        {(cv.profile?.contact || []).map((item, index) => (
          <div key={`contact-${index}`} style={{ ...blockStyle, display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'start' }}>
            <input style={fieldStyle} placeholder="Text" value={item.text || ''} onChange={(e) => updateContact(index, 'text', e.target.value)} />
            <input style={fieldStyle} placeholder="Link (optional, e.g. mailto:...)" value={item.href || ''} onChange={(e) => updateContact(index, 'href', e.target.value)} />
            <div>
              <button type="button" style={btnStyle} onClick={() => moveContact(index, -1)}>↑</button>
              <button type="button" style={btnStyle} onClick={() => moveContact(index, 1)}>↓</button>
              <button type="button" style={btnStyle} onClick={() => removeContact(index)}>Remove</button>
            </div>
          </div>
        ))}
        <button type="button" style={btnStyle} onClick={addContact}>Add contact line</button>
      </div>

      <div style={{ marginBottom: 10 }}>
        <strong>Intro paragraphs</strong>
        {(cv.profile?.intro || []).map((paragraph, index) => (
          <div key={`intro-${index}`} style={blockStyle}>
            <textarea style={fieldStyle} rows={2} value={paragraph} onChange={(e) => updateIntro(index, e.target.value)} />
            <button type="button" style={btnStyle} onClick={() => moveIntro(index, -1)}>↑</button>
            <button type="button" style={btnStyle} onClick={() => moveIntro(index, 1)}>↓</button>
            <button type="button" style={btnStyle} onClick={() => removeIntro(index)}>Remove</button>
          </div>
        ))}
        <button type="button" style={btnStyle} onClick={addIntro}>Add intro paragraph</button>
      </div>

      <h4>Sections</h4>
      <div className="admin-toolbar" style={{ borderBottom: '1px solid #000', marginBottom: 16, paddingBottom: 12 }}>
        <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 8 }}>
          Quick add
        </span>
        {CV_SECTION_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className="admin-btn admin-btn--ghost"
            onClick={() => quickAddToPreset(preset.key)}
          >
            + {preset.createLabel}
          </button>
        ))}
      </div>

      {sortedSectionIndices.map((sectionIndex) => {
        const section = sections[sectionIndex];
        const preset = findPresetForSection(section);
        const sectionType = section.type || 'entries';
        const addLabel =
          sectionType === 'list'
            ? `Add ${section.label || 'item'}`
            : `Add ${section.label || 'entry'}`;

        return (
          <div key={`section-${sectionIndex}`} style={blockStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
              <input style={fieldStyle} placeholder="Section label" value={section.label || ''} onChange={(e) => updateSection(sectionIndex, { label: e.target.value })} />
              <select style={fieldStyle} value={sectionType} onChange={(e) => updateSection(sectionIndex, { type: e.target.value })}>
                <option value="entries">Entries (Education, Experience, Certifications)</option>
                <option value="list">List (Skills)</option>
              </select>
              <div>
                <button type="button" style={btnStyle} onClick={() => moveSection(sectionIndex, -1)}>↑</button>
                <button type="button" style={btnStyle} onClick={() => moveSection(sectionIndex, 1)}>↓</button>
                <button type="button" style={btnStyle} onClick={() => removeSection(sectionIndex)}>Remove section</button>
              </div>
            </div>

            {preset && (
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666' }}>
                {preset.key === 'experience' && 'Add roles with title, company, location, dates, and description.'}
                {preset.key === 'education' && 'Add schools, degrees, and dates.'}
                {preset.key === 'skills' && 'Add individual skills or tools.'}
                {preset.key === 'certifications' && 'Add certification name and issuing organization.'}
              </p>
            )}

            {sectionType === 'list' ? (
              <>
                {(section.items || []).length === 0 && (
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#666' }}>No skills yet.</p>
                )}
                {(section.items || []).map((item, itemIndex) => (
                  <div key={`item-${itemIndex}`} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <input style={{ ...fieldStyle, flex: 1 }} placeholder="Skill / item" value={item} onChange={(e) => updateListItem(sectionIndex, itemIndex, e.target.value)} />
                    <button type="button" style={btnStyle} onClick={() => moveListItem(sectionIndex, itemIndex, -1)}>↑</button>
                    <button type="button" style={btnStyle} onClick={() => moveListItem(sectionIndex, itemIndex, 1)}>↓</button>
                    <button type="button" style={btnStyle} onClick={() => removeListItem(sectionIndex, itemIndex)}>Remove</button>
                  </div>
                ))}
                <button type="button" className="admin-btn admin-btn--ghost" style={btnStyle} onClick={() => addListItem(sectionIndex)}>
                  {addLabel}
                </button>
                <input style={{ ...fieldStyle, marginTop: 8 }} placeholder="Footer (e.g. Languages: English, French)" value={section.footer || ''} onChange={(e) => updateSection(sectionIndex, { footer: e.target.value })} />
              </>
            ) : (
              <>
                {(section.entries || []).length === 0 && (
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#666' }}>No entries yet.</p>
                )}
                {(section.entries || []).map((entry, entryIndex) => (
                  <div key={`entry-${entryIndex}`} style={{ border: '1px solid #000', borderRadius: 0, padding: 8, marginBottom: 8, background: '#fff', color: '#000' }}>
                    <input style={{ ...fieldStyle, marginBottom: 6 }} placeholder="Title" value={entry.title || ''} onChange={(e) => updateEntry(sectionIndex, entryIndex, 'title', e.target.value)} />
                    <input style={{ ...fieldStyle, marginBottom: 6 }} placeholder="Subtitle" value={entry.subtitle || ''} onChange={(e) => updateEntry(sectionIndex, entryIndex, 'subtitle', e.target.value)} />
                    <input style={{ ...fieldStyle, marginBottom: 6 }} placeholder="Meta (location, org)" value={entry.meta || ''} onChange={(e) => updateEntry(sectionIndex, entryIndex, 'meta', e.target.value)} />
                    <input style={{ ...fieldStyle, marginBottom: 6 }} placeholder="Date" value={entry.date || ''} onChange={(e) => updateEntry(sectionIndex, entryIndex, 'date', e.target.value)} />
                    {isExperienceSection(section) && (
                      <>
                        <strong>Description</strong>
                        <p style={{ margin: '4px 0 8px', fontSize: 12, color: '#666' }}>
                          Add one block per role or bullet. Press Enter for multiple lines within a block.
                        </p>
                        {(entry.details || []).map((detail, detailIndex) => (
                          <div key={`detail-${detailIndex}`} style={{ marginTop: 4 }}>
                            <textarea
                              style={{ ...fieldStyle, minHeight: 72, resize: 'vertical' }}
                              rows={3}
                              placeholder="Job description (multiple lines allowed)"
                              value={detail}
                              onChange={(e) => updateEntryDetail(sectionIndex, entryIndex, detailIndex, e.target.value)}
                            />
                            <button type="button" style={btnStyle} onClick={() => removeEntryDetail(sectionIndex, entryIndex, detailIndex)}>Remove</button>
                          </div>
                        ))}
                        <button type="button" style={btnStyle} onClick={() => addEntryDetail(sectionIndex, entryIndex)}>Add description block</button>
                      </>
                    )}
                    <div style={{ marginTop: 6 }}>
                      <button type="button" style={btnStyle} onClick={() => moveEntry(sectionIndex, entryIndex, -1)}>↑</button>
                      <button type="button" style={btnStyle} onClick={() => moveEntry(sectionIndex, entryIndex, 1)}>↓</button>
                      <button type="button" style={btnStyle} onClick={() => removeEntry(sectionIndex, entryIndex)}>Remove entry</button>
                    </div>
                  </div>
                ))}
                <button type="button" className="admin-btn admin-btn--ghost" style={btnStyle} onClick={() => addEntry(sectionIndex)}>
                  {addLabel}
                </button>
              </>
            )}
          </div>
        );
      })}

      <button type="button" style={btnStyle} onClick={addSection}>Add custom section</button>

      <div style={{ marginTop: 12 }}>
        <input style={fieldStyle} placeholder="Last updated (e.g. 22.05.26)" value={cv.lastUpdated || ''} onChange={(e) => onChange({ ...cv, lastUpdated: e.target.value })} />
      </div>
    </div>
  );
}
